/**
 * Idle-wake session re-verification.
 *
 * A device left open on the portal outlives its refresh token; without this,
 * the first interaction after waking fires an already-doomed request (seen in
 * the collector as ChangeUser 400s on stale sessions) and the user works
 * against a dead session until something forces a logout. When the tab
 * becomes visible again after a meaningful idle period, probe the backend
 * with a token refresh: alive rotates the access token, dead forces a clean
 * logout to /login before the user can interact with stale UI.
 */

const MIN_HIDDEN_MS = 60_000;

export default defineNuxtPlugin((nuxtApp) => {
	let hiddenAt: number | null = null;
	let probing = false;

	async function verifySessionAfterWake(idleMs: number): Promise<void> {
		const authState = useState<{ isLoggedIn: boolean }>('lcp-auth');
		if (!authState.value?.isLoggedIn) return;

		// Supplier portals have no refresh token; a probe would always fail
		// and log out a still-valid session.
		const { portalType } = useCustomerConfig();
		if (portalType.value === 'supplier') return;

		const { $apiAuth } = useNuxtApp();
		const newToken = await $apiAuth.attemptTokenRefresh();
		if (newToken) {
			$apiAuth.authenticate(newToken);
			frogger.debug('session re-verified after idle wake', { idleMs });
			return;
		}

		// A failed probe while offline is a network drop, not session death.
		if (!navigator.onLine) {
			frogger.debug('idle-wake session probe skipped: offline', { idleMs });
			return;
		}

		frogger.warn('session dead after idle wake', { idleMs });
		$apiAuth.forceLogout('idle-wake-session-dead');
	}

	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') {
			hiddenAt = Date.now();
			return;
		}

		const idleMs = hiddenAt === null ? 0 : Date.now() - hiddenAt;
		hiddenAt = null;

		// Test hook: e2e cannot wait out the real threshold, so allow an
		// override injected via addInitScript. Undefined in production.
		const minHiddenMs =
			(window as { __lcpIdleRecheckMs?: number }).__lcpIdleRecheckMs ?? MIN_HIDDEN_MS;
		if (idleMs < minHiddenMs || probing) return;

		probing = true;
		// The handler runs outside the Nuxt lifecycle, so composables inside
		// verifySessionAfterWake need the app context restored explicitly.
		void nuxtApp
			.runWithContext(() => verifySessionAfterWake(idleMs))
			.finally(() => {
				probing = false;
			});
	});
});
